import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import type { Facet } from "@/features/resources/resources";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

/**
 * One axis of the library, as a dropdown.
 *
 * It was a row of chips, capped at twelve. That put the shape of the library
 * on the page, which is worth something, but its height grew with the
 * vocabulary: on a phone the three axes ran most of a screen before the first
 * result, and tags are member-supplied and unbounded, so it was going to get
 * worse rather than better. A dropdown is the same height whatever it holds.
 *
 * The counts come with the options rather than being lost. They are computed
 * against the results narrowed by every filter *except* this one, so every
 * option here would leave something on the page and none of them empties it.
 *
 * An axis with nothing to offer renders nothing at all. That is how the
 * location filter disappears under Funding without anything knowing that
 * Funding has no locations.
 */

/** Radix has no empty-string item, so "no filter" needs a value of its own. */
const ALL = "__all__";

export function FacetSelect({
  label,
  facets,
  selected,
  onSelect,
  preview = false,
}: {
  label: string;
  facets: Facet[];
  selected: string | null;
  onSelect: (value: string | null) => void;
  /** Somebody who is not a member: the same control, opening to the same
   *  counts, but choosing one asks them to join rather than filtering.
   *  Filtering is off in the preview for the reason search is — the list is a
   *  capped sample, and an axis you can narrow is a way of walking past the
   *  cap one facet at a time. */
  preview?: boolean;
}) {
  const navigate = useNavigate();
  if (!facets.length) return null;
  return (
    <Select
      // Empty string is "nothing chosen" on the root, which is what shows the
      // placeholder. Only *items* may not carry it, hence ALL below.
      value={selected ?? ""}
      onValueChange={(value) => {
        if (preview) {
          toast("The resource library is for members", {
            description:
              "You can see what is in it. Filtering and the links come with membership.",
            action: { label: "Apply", onClick: () => navigate("/apply") },
          });
          return;
        }
        onSelect(value === ALL ? null : value);
      }}
    >
      <SelectTrigger aria-label={label} className="w-full">
        {/* Unset, the trigger is the name of the axis, so three closed
            dropdowns still say what you can filter by. */}
        <SelectValue placeholder={label} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>Any</SelectItem>
        {facets.map((facet) => (
          <SelectItem key={facet.value} value={facet.value}>
            {facet.value}{" "}
            <span className="text-muted-foreground tabular-nums">{facet.count}</span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export default FacetSelect;
